import { z } from 'zod';

/* ─────────── Общий журнал работ из принятых отчётов площадки (ДК-07, раздел 6.15) ─────────── */

/**
 * Журналы работ (ДК-07, ПР-09): общий — все принятые работы; специальные — работы своего вида. Вид работы берётся из
 * перечня исполнительной документации (ДК-06): шаблон, применённый к работе графика, задаёт вид («Бетонирование»,
 * «Сварка», «Изоляция»). Своих записей у журналов нет — только принятые отчёты смены; печать по утверждённым формам
 * появится вместе с шаблонами форм компании.
 */
export const WORK_JOURNAL_KINDS = {
  general: 'Общий журнал работ',
  concrete: 'Журнал бетонных работ',
  welding: 'Журнал сварочных работ',
  insulation: 'Журнал антикоррозионных и изоляционных работ',
} as const;
export type WorkJournalKind = keyof typeof WORK_JOURNAL_KINDS;
/** Какие виды работ шаблонов ИД (`CHECKLIST_WORK_KINDS`) попадают в специальный журнал */
export const WORK_JOURNAL_WORK_KINDS: Record<Exclude<WorkJournalKind, 'general'>, readonly string[]> = {
  concrete: ['concrete'],
  welding: ['welding'],
  // Отдельного вида «антикоррозия» в шаблонах нет (CHECKLIST_WORK_KINDS): антикоррозионные работы относят к журналу
  // шаблоном вида «Изоляция»; отдельный вид — запрос в docs/streams.md
  insulation: ['insulation'],
};

export const WorkJournalQuery = z.strictObject({
  projectId: z.uuid(),
  kind: z.enum(Object.keys(WORK_JOURNAL_KINDS) as [WorkJournalKind, ...WorkJournalKind[]]).default('general'),
  objectId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
export type WorkJournalQuery = z.infer<typeof WorkJournalQuery>;

/** Одна запись журнала — одна работа из принятого отчёта смены. Суммы денег в журнал не попадают. */
export interface WorkJournalEntry {
  /** Сквозной номер записи в выбранном периоде */
  no: number;
  reportId: string;
  lineId: string;
  day: string;
  shift: string;
  objectId: string | null;
  objectName: string | null;
  activityId: string;
  activityName: string;
  packageName: string;
  unit: string;
  /** Принятый объём (строкой, без потери точности) */
  quantity: string;
  /** Что заявлено на площадке, если принято иное */
  claimed: string;
  people: number;
  machines: number;
  photos: number;
  /** Материалы, списанные по этой работе (из отчёта смены, без цен) */
  materials: WorkJournalMaterial[];
  authorName: string;
  note: string | null;
}

export interface WorkJournalMaterial {
  itemId: string;
  name: string;
  quantity: string;
  unit: string;
}

export interface WorkJournal {
  kind: WorkJournalKind;
  /** Для специального журнала: сколько работ проекта отнесено к его виду через перечень ИД (для общего — null) */
  kindActivities: number | null;
  projectId: string;
  projectCode: string;
  projectName: string;
  from: string;
  to: string;
  entries: WorkJournalEntry[];
  /** Принятые отчёты, попавшие в журнал */
  reportsCount: number;
  /** Отчёты за период, которые ещё не приняты и потому в журнал не вошли */
  pendingReports: number;
  /** Дни периода, в которых нет ни одного принятого отчёта (только рабочие дни по графику не считаются — просто дни) */
  daysWithoutEntries: number;
}

/* ─────────── Журнал входного контроля (ДК-07, ПР-09): приёмка на площадке и решение ОТК склада ─────────── */

export const IncomingJournalQuery = z.strictObject({
  projectId: z.uuid(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
export type IncomingJournalQuery = z.infer<typeof IncomingJournalQuery>;

/**
 * Одна запись: либо приёмка поставки на площадке (количество, накладная, паспорт, замечание поставщику), либо решение
 * входного контроля по строке прихода на склад (сертификат, паспорт, протокол, решение ОТК). Цен нет.
 */
export interface IncomingJournalEntry {
  no: number;
  source: 'site' | 'warehouse';
  /** id приёмки площадки или строки прихода склада */
  id: string;
  day: string;
  material: string;
  quantity: string;
  unit: string | null;
  supplierName: string | null;
  contractId: string | null;
  contractNumber: string | null;
  /** Накладная (площадка) или номер приходного документа (склад) */
  document: string | null;
  /** Паспорт, сертификат: номер или «есть / нет» */
  quality: string;
  /** Результат: принято, нет паспорта — замечание, на контроле, принято ОТК, отклонено */
  result: string;
  remarkId: string | null;
  photos: number;
  personName: string | null;
}

export interface IncomingJournal {
  projectId: string;
  projectCode: string;
  projectName: string;
  from: string;
  to: string;
  entries: IncomingJournalEntry[];
  /** Склад отдал не все строки (предел выборки) — период стоит сузить */
  truncated: boolean;
}
